import { zodResolver } from '@hookform/resolvers/zod'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createFileRoute, getRouteApi, Link, useNavigate } from '@tanstack/react-router'
import { ArrowLeft, Trash2, Users } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { toast } from 'sonner'
import { adminLogQueries } from '@/api/endpoints/admin-logs'
import { deleteTeam, teamQueries, updateTeam } from '@/api/endpoints/teams'
import { apiErrorMessage } from '@/api/errors'
import { RoleGate } from '@/components/kit/role-gate'
import { ConfirmDialog } from '@/components/kit/confirm-dialog'
import { Form } from '@/components/kit/form-fields'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { AuditPanel } from '@/features/audit/audit-panel'
import { TeamDetailsFields } from '@/features/teams/team-details-fields'
import { TeamLogoPanel } from '@/features/teams/team-logo-panel'
import { teamFormSchema, type TeamFormValues } from '@/features/teams/team-form-schema'
import { TeamSocialMediaPanel } from '@/features/teams/team-social-media-panel'
import { toTeamFormValues, toTeamUpdateInput } from '@/features/teams/to-team-write'

export const Route = createFileRoute('/_authed/teams/$uuid')({
  loader: ({ context, params }) =>
    context.queryClient.prefetchQuery(teamQueries.detail(params.uuid)),
  component: TeamEditPage,
})

const teamsRoute = getRouteApi('/_authed/teams')

function TeamEditPage() {
  const { uuid } = Route.useParams()
  // Keyed by uuid so form state and mutations never carry over from another team.
  return <TeamEdit key={uuid} uuid={uuid} />
}

function TeamEdit({ uuid }: { uuid: string }) {
  const listSearch = teamsRoute.useSearch()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { data: team, isLoading, isError, error } = useQuery(teamQueries.detail(uuid))
  const [confirmDelete, setConfirmDelete] = useState(false)

  const form = useForm<TeamFormValues>({
    resolver: zodResolver(teamFormSchema),
    defaultValues: { name: '', tag: '', server: '', creationDate: '' },
  })

  const { isDirty } = form.formState

  useEffect(() => {
    if (team && !isDirty) form.reset(toTeamFormValues(team))
  }, [team, form, isDirty])

  const save = useMutation({
    mutationFn: (values: TeamFormValues) => {
      if (!team) throw new Error('Team not loaded')
      return updateTeam(uuid, toTeamUpdateInput(team, values))
    },
    onSuccess: (updated) => {
      queryClient.setQueryData(teamQueries.detail(uuid).queryKey, updated)
      void queryClient.invalidateQueries({ queryKey: teamQueries.lists })
      toast.success('Team saved')
      form.reset(toTeamFormValues(updated))
    },
    onError: (e) => toast.error(apiErrorMessage(e)),
  })

  const remove = useMutation({
    mutationFn: () => deleteTeam(uuid),
    onSuccess: async () => {
      toast.success('Team deleted')
      await navigate({ to: '/teams', search: listSearch })
      // Remove the cached data only after leaving the screen, so nothing refetches a deleted team.
      queryClient.removeQueries({ queryKey: teamQueries.detail(uuid).queryKey })
      queryClient.removeQueries({ queryKey: teamQueries.socialMedia(uuid).queryKey })
      queryClient.removeQueries({ queryKey: adminLogQueries.entity(uuid).queryKey })
      void queryClient.invalidateQueries({ queryKey: teamQueries.lists })
    },
    onError: (e) => toast.error(apiErrorMessage(e)),
  })

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-40 w-full max-w-xl" />
      </div>
    )
  }

  if (isError || !team) {
    return (
      <div className="space-y-4">
        <Button variant="ghost" asChild>
          <Link to="/teams" search={listSearch}>
            <ArrowLeft />
            Back to teams
          </Link>
        </Button>
        <p role="alert" className="text-destructive">
          {apiErrorMessage(error ?? new Error('Team not found'))}
        </p>
      </div>
    )
  }

  // Admin-v2 only offers delete when the roster is empty. Remove members from the Members screen.
  const canDelete = (team.members?.length ?? 0) === 0

  return (
    <div className="mx-auto max-w-3xl space-y-6">
      <div className="flex flex-wrap items-center gap-3">
        <Button variant="ghost" size="sm" asChild>
          <Link to="/teams" search={listSearch}>
            <ArrowLeft />
            Teams
          </Link>
        </Button>
        <h1 className="text-2xl font-semibold">{team.name}</h1>
        <Button variant="outline" size="sm" className="ml-auto" asChild>
          <Link to="/teams/$uuid/members" params={{ uuid }} search={listSearch}>
            <Users />
            Members
          </Link>
        </Button>
      </div>

      <Form form={form} onSubmit={(values) => save.mutate(values)} className="grid gap-4">
        <TeamDetailsFields currentServer={team.server} />
        <div className="flex flex-wrap gap-2 pt-2">
          <Button type="submit" disabled={save.isPending || !isDirty}>
            Save
          </Button>
          {canDelete && (
            <Button type="button" variant="destructive" onClick={() => setConfirmDelete(true)}>
              <Trash2 />
              Delete
            </Button>
          )}
        </div>
      </Form>

      <TeamLogoPanel teamUuid={uuid} logoUrl={team.logo?.url} />
      <TeamSocialMediaPanel teamUuid={uuid} />
      <RoleGate role="ROLE_SUPER_ADMIN">
        <AuditPanel entityUuid={uuid} description="Logged changes to this team and its roster" />
      </RoleGate>

      <ConfirmDialog
        open={confirmDelete}
        onOpenChange={setConfirmDelete}
        title="Delete this team?"
        description="This deletes the team and its tournament participations."
        confirmLabel="Delete"
        destructive
        onConfirm={() => remove.mutateAsync()}
      />
    </div>
  )
}
